import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiLayers, FiRefreshCw, FiTarget, FiBarChart2, FiSettings, FiGlobe } from 'react-icons/fi';
import { signUpTrial, logOut } from '../services/authService';
import { LogoLockup } from '../components/LogoMark';
import '../styles/landing.css';

// ── Data ──────────────────────────────────────────────────────────────────────

const STATS = [
  { value: 'Dozens', label: 'Built-In Decks' },
  { value: '250+',   label: 'Flashcard Variations' },
  { value: '100%',   label: 'Evidence-Based ABA' },
  { value: 'Free',   label: 'To Start' },
];

const SAMPLE_CARDS = [
  { label: 'Dog',      pxId: 31203405 },
  { label: 'Cat',      pxId: 6864659  },
  { label: 'Horse',    pxId: 30287738 },
  { label: 'Elephant', pxId: 25754094 },
  { label: 'Bird',     pxId: 30583571 },
  { label: 'Rabbit',   pxId: 31778632 },
];

const FEATURES = [
  {
    icon: <FiLayers size={22} />,
    title: 'Stimulus Decks',
    desc: 'Choose from dozens of built-in decks across Animals, Food, Colors, Actions, Emotions, and more — each with rich, real-photo stimuli ready to use.',
  },
  {
    icon: <FiRefreshCw size={22} />,
    title: 'Within-Stimulus Variation',
    desc: 'Target cards rotate through multiple versions of the same concept, teaching true generalization — not just memorization of a single image.',
  },
  {
    icon: <FiTarget size={22} />,
    title: 'Smart Distractors',
    desc: 'Distractors are always pulled from the same category, creating clinically appropriate trial difficulty without manual setup every session.',
  },
  {
    icon: <FiBarChart2 size={22} />,
    title: 'Session Tracking',
    desc: 'Every trial is recorded automatically. View accuracy rates, response patterns, and progress over time in clear, exportable reports.',
  },
  {
    icon: <FiSettings size={22} />,
    title: 'Fully Configurable',
    desc: 'Set number of trials, display mode, distractor count, and target selection method before every session — your protocol, your way.',
  },
  {
    icon: <FiGlobe size={22} />,
    title: 'Community Decks',
    desc: 'Browse decks created by other therapists and share your own. The library grows with every practitioner who uses Stimuli Central.',
  },
];

const WHO = [
  {
    title: 'Clinics & ABA Practices',
    desc: 'Give your therapy team a consistent, data-driven tool for running discrete trial training across all clients and skill levels.',
    photo: 'https://images.pexels.com/photos/4021775/pexels-photo-4021775.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
  },
  {
    title: 'Schools & Special Education',
    desc: 'Support IEP goals with structured stimulus trials that teachers and aides can run right in the classroom — no training required.',
    photo: 'https://images.pexels.com/photos/5212345/pexels-photo-5212345.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
  },
  {
    title: 'Caregivers & Families',
    desc: 'Empower parents and caregivers to continue therapy work at home with the same evidence-based tools their child\'s therapist uses.',
    photo: 'https://images.pexels.com/photos/7155800/pexels-photo-7155800.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
  },
];

const STEPS = [
  {
    num: '1',
    title: 'Choose a Deck',
    desc: 'Pick from built-in stimulus decks or create your own with custom images, labels, and photo variations.',
  },
  {
    num: '2',
    title: 'Configure Your Session',
    desc: 'Set the number of trials, display mode, target selection method, and distractor options in seconds.',
  },
  {
    num: '3',
    title: 'Run & Track',
    desc: 'Conduct trials and watch results update in real time. Review accuracy and patterns when the session ends.',
  },
];

const TESTIMONIALS = [
  {
    quote: "Stimuli Central has completely changed how I run DTT. My clients are more engaged and my session data is actually clean for the first time.",
    name: "Sarah M.",
    role: "BCBA · 8 years experience",
    initial: "S",
  },
  {
    quote: "We use it in our classroom every day. It took ten minutes to set up and the visual variety keeps students from just memorizing a single image.",
    name: "Jordan T.",
    role: "Special Education Teacher",
    initial: "J",
  },
  {
    quote: "As a parent I can do the same drills at home that the therapist does at the clinic. That consistency has made a real difference for our son.",
    name: "Maria R.",
    role: "Parent & Caregiver",
    initial: "M",
  },
];

const FAQS = [
  {
    q: 'What is Stimuli Central?',
    a: 'Stimuli Central is a digital flashcard platform built specifically for ABA (Applied Behavior Analysis) therapy. It helps therapists, educators, and caregivers run structured discrete trial training sessions with consistent, research-aligned stimuli.',
  },
  {
    q: 'Do I need to know ABA to use it?',
    a: 'No. The app is designed to be simple enough for caregivers with no clinical background, while powerful enough for BCBAs running formal programs. Built-in decks and automatic data collection do the heavy lifting.',
  },
  {
    q: 'What is within-stimulus variation and why does it matter?',
    a: 'Within-stimulus variation means each target concept (e.g., "Apple") is taught using multiple different photos of that concept. This prevents rote memorization of a single image and builds true generalization — a core principle in modern ABA.',
  },
  {
    q: 'Is my data stored securely?',
    a: 'Yes. All session data is stored in Firebase with industry-standard security. Your data never leaves your account and is never shared. Do not enter real learner names or identifying information — use initials or codes only. You are responsible for ensuring HIPAA compliance in your practice.',
  },
  {
    q: 'Can I create my own decks?',
    a: 'Absolutely. You can build custom decks with your own images and labels, and optionally share them with the Stimuli Central community.',
  },
  {
    q: 'How does the free trial work?',
    a: 'Sign up with just your email and a password. You get full access for 2 weeks. After that you can subscribe to keep your data and continue using the platform.',
  },
];

// ── Nav ────────────────────────────────────────────────────────────────────────
const LandingNav = ({ onScrollTo, navigate, onSignUp }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <nav className="lp-nav">
      <div className="lp-nav-inner">
        <a href="#hero" className="lp-nav-logo" onClick={(e) => { e.preventDefault(); onScrollTo('hero'); setMenuOpen(false); }}>
          <LogoLockup size={28} />
        </a>

        <div className={`lp-nav-links ${menuOpen ? 'lp-nav-links--open' : ''}`}>
          <button className="lp-nav-link" onClick={() => { onScrollTo('features'); setMenuOpen(false); }}>Features</button>
          <button className="lp-nav-link" onClick={() => { onScrollTo('who'); setMenuOpen(false); }}>Who It's For</button>
          <button className="lp-nav-link" onClick={() => { onScrollTo('how'); setMenuOpen(false); }}>How It Works</button>
          <button className="lp-nav-link" onClick={() => { onScrollTo('faq'); setMenuOpen(false); }}>FAQ</button>
        </div>

        <div className="lp-nav-actions">
          <button className="lp-nav-login" onClick={() => navigate('/login')}>Log In</button>
          <button className="lp-nav-cta" onClick={() => { onSignUp(); setMenuOpen(false); }}>Sign Up Free</button>
          <button
            className="lp-nav-hamburger"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lp-nav-mobile-menu">
          <button className="lp-nav-mobile-link" onClick={() => { onScrollTo('features'); setMenuOpen(false); }}>Features</button>
          <button className="lp-nav-mobile-link" onClick={() => { onScrollTo('who'); setMenuOpen(false); }}>Who It's For</button>
          <button className="lp-nav-mobile-link" onClick={() => { onScrollTo('how'); setMenuOpen(false); }}>How It Works</button>
          <button className="lp-nav-mobile-link" onClick={() => { onScrollTo('faq'); setMenuOpen(false); }}>FAQ</button>
          <button className="lp-nav-mobile-link lp-nav-mobile-link--cta" onClick={() => { onSignUp(); setMenuOpen(false); }}>Sign Up Free →</button>
        </div>
      )}
    </nav>
  );
};

// ── Trial Expired Banner ───────────────────────────────────────────────────────
const TrialExpiredBanner = ({ onDismiss, onScrollTo }) => (
  <div className="lp-trial-expired-banner">
    <span className="lp-trial-expired-text">
      Your 2-week trial has ended — ready to get started for real?
    </span>
    <button className="lp-trial-expired-cta" onClick={() => onScrollTo('try')}>
      Sign up now →
    </button>
    <button className="lp-trial-expired-dismiss" onClick={onDismiss} title="Dismiss">✕</button>
  </div>
);

// ── FAQ Item ──────────────────────────────────────────────────────────────────
const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`lp-faq-item ${open ? 'lp-faq-item--open' : ''}`}>
      <button className="lp-faq-q" onClick={() => setOpen(!open)}>
        <span>{q}</span>
        <span className="lp-faq-chevron">{open ? '−' : '+'}</span>
      </button>
      {open && <p className="lp-faq-a">{a}</p>}
    </div>
  );
};

// ── Shared sign-up form logic ──────────────────────────────────────────────────
const useSignUpForm = (navigate, onSuccess) => {
  const [email,    setEmail]    = useState('');
  const [name,     setName]     = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !name.trim() || !password) {
      setError('Please fill in all fields.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    setError('');
    const result = await signUpTrial(email.trim(), name.trim(), password);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return { email, setEmail, name, setName, password, setPassword, loading, error, handleSubmit };
};

// ── Sign-Up Form Fields (shared between modal and page section) ────────────────
const SignUpFields = ({ email, setEmail, name, setName, password, setPassword, loading, error, handleSubmit, idPrefix = 'try' }) => (
  <form className="lp-try-form" onSubmit={handleSubmit} noValidate>
    <div className="lp-try-field">
      <label className="lp-try-label" htmlFor={`${idPrefix}-name`}>Full Name</label>
      <input
        id={`${idPrefix}-name`}
        className="lp-try-input"
        type="text"
        placeholder="Your full name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoComplete="name"
        required
      />
    </div>
    <div className="lp-try-field">
      <label className="lp-try-label" htmlFor={`${idPrefix}-email`}>Email Address</label>
      <input
        id={`${idPrefix}-email`}
        className="lp-try-input"
        type="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="email"
        required
      />
    </div>
    <div className="lp-try-field">
      <label className="lp-try-label" htmlFor={`${idPrefix}-password`}>Password</label>
      <input
        id={`${idPrefix}-password`}
        className="lp-try-input"
        type="password"
        placeholder="At least 6 characters"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
        maxLength={128}
        required
      />
    </div>
    {error && <p className="lp-try-error">{error}</p>}
    <button className="lp-try-submit" type="submit" disabled={loading}>
      {loading ? 'Creating your account…' : 'Sign Up Now — Free Trial'}
    </button>
    <p className="lp-try-fine-print">
      2-week trial. Full access from day one.
    </p>
  </form>
);

// ── Sign-Up Modal (triggered from nav CTA) ─────────────────────────────────────
const SignUpModal = ({ navigate, onClose }) => {
  const form = useSignUpForm(navigate, onClose);

  // Close on Escape key
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="lp-signup-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="lp-signup-modal" onClick={(e) => e.stopPropagation()}>
        <button className="lp-signup-modal-close" onClick={onClose} aria-label="Close">×</button>
        <h2 className="lp-signup-modal-title">Create Your Free Account</h2>
        <p className="lp-signup-modal-sub">Full access for 2 weeks — sign up in seconds.</p>
        <SignUpFields {...form} idPrefix="modal" />
      </div>
    </div>
  );
};

// ── Try It Free Section ────────────────────────────────────────────────────────
const TrySection = ({ navigate }) => {
  const form = useSignUpForm(navigate);

  return (
    <section id="try" className="lp-try">
      <div className="lp-try-layout">

        {/* Left: pitch */}
        <div className="lp-try-pitch">
          <span className="lp-section-label">Free Trial</span>
          <h2 className="lp-try-pitch-title">Sign Up Now — Start Free</h2>
          <p className="lp-try-pitch-sub">
            Get full access for 2 weeks and see exactly how
            Stimuli Central works in a real session.
          </p>
          <ul className="lp-try-pitch-list">
            <li>All built-in stimulus decks — ready immediately</li>
            <li>Unlimited trial sessions for 2 weeks</li>
            <li>Automatic session data and reports</li>
            <li>Custom deck builder with your own photos</li>
            <li>No charges, no commitments</li>
          </ul>
        </div>

        {/* Right: sign-up form */}
        <div className="lp-try-card">
          <h3 className="lp-try-card-title">Create Your Free Account</h3>
          <SignUpFields {...form} idPrefix="page" />
        </div>

      </div>
    </section>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
export const LandingPage = ({ trialExpired = false }) => {
  const navigate = useNavigate();
  const [expiredDismissed, setExpiredDismissed] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);

  useEffect(() => {
    if (trialExpired) logOut();
  }, [trialExpired]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing">
      {trialExpired && !expiredDismissed && (
        <TrialExpiredBanner
          onDismiss={() => setExpiredDismissed(true)}
          onScrollTo={scrollTo}
        />
      )}

      {showSignUpModal && (
        <SignUpModal navigate={navigate} onClose={() => setShowSignUpModal(false)} />
      )}

      <LandingNav onScrollTo={scrollTo} navigate={navigate} onSignUp={() => setShowSignUpModal(true)} />

      {/* ── Hero ── */}
      <section id="hero" className="lp-hero">
        <img
          className="lp-hero-bg-photo"
          src="https://images.pexels.com/photos/256417/pexels-photo-256417.jpeg?auto=compress&cs=tinysrgb&w=1200&h=700&fit=crop"
          alt=""
          aria-hidden="true"
        />

        <div className="lp-hero-inner">

          {/* Left copy */}
          <div className="lp-hero-text">
            <span className="lp-hero-eyebrow">ABA Therapy Platform</span>
            <h1 className="lp-hero-headline">
              Therapy materials<br />
              that work as hard<br />
              <span className="lp-hero-headline-accent">as you do.</span>
            </h1>
            <p className="lp-hero-sub">
              Stimuli Central gives therapists, educators, and caregivers the tools to
              create engaging sessions, track progress, and teach with confidence.
            </p>
            <div className="lp-hero-buttons">
              <button className="lp-btn-primary lp-btn-primary--lg" onClick={() => setShowSignUpModal(true)}>
                Sign Up Free — 2 Week Trial
              </button>
              <button className="lp-btn-outline lp-btn-outline--lg" onClick={() => scrollTo('how')}>
                See How It Works
              </button>
            </div>
            <div className="lp-hero-trust">
              <span className="lp-trust-item"><span className="lp-trust-icon lp-trust-icon--check">✓</span>2-week full access</span>
              <span className="lp-trust-item"><span className="lp-trust-icon lp-trust-icon--check">✓</span>Full access</span>
              <span className="lp-trust-item"><span className="lp-trust-icon lp-trust-icon--check">✓</span>2-week trial</span>
            </div>
          </div>

          {/* Right: app mockup */}
          <div className="lp-hero-visual">
            <div className="lp-app-mockup">
              <div className="lp-mock-header">
                <button className="lp-mock-back">← Back to Sessions</button>
                <button className="lp-mock-end">End Session</button>
              </div>

              <div className="lp-mock-body">
                <div className="lp-mock-main">
                  <div className="lp-mock-deck-title">
                    <span className="lp-mock-deck-name">Animals</span>
                    <span className="lp-mock-deck-count">Cards in this set: 24</span>
                  </div>

                  <div className="lp-mock-cards">
                    {[
                      { id: 31203405, label: 'Dog',      selected: true  },
                      { id: 6864659,  label: 'Cat',      selected: false },
                      { id: 30287738, label: 'Horse',    selected: false },
                      { id: 25754094, label: 'Elephant', selected: false },
                    ].map(card => (
                      <div key={card.label} className={`lp-mock-card${card.selected ? ' lp-mock-card--selected' : ''}`}>
                        {card.selected && <span className="lp-mock-card-check">✓</span>}
                        <img
                          src={`https://images.pexels.com/photos/${card.id}/pexels-photo-${card.id}.jpeg?auto=compress&cs=tinysrgb&w=160&h=140&fit=crop`}
                          alt={card.label}
                          className="lp-mock-card-img"
                        />
                        <span className="lp-mock-card-label">{card.label}</span>
                      </div>
                    ))}
                  </div>

                  <div className="lp-mock-correct-banner">
                    <span className="lp-mock-correct-icon">✓</span> Correct!
                  </div>
                </div>

                <div className="lp-mock-sidebar">
                  <div className="lp-mock-stat">
                    <span className="lp-mock-stat-label">Progress</span>
                    <span className="lp-mock-stat-val">8 / 24</span>
                    <div className="lp-mock-progress-bar"><div className="lp-mock-progress-fill" style={{ width: '33%' }} /></div>
                  </div>
                  <div className="lp-mock-stat">
                    <span className="lp-mock-stat-label">Accuracy</span>
                    <span className="lp-mock-stat-val lp-mock-stat-val--green">91%</span>
                    <svg className="lp-mock-chart" viewBox="0 0 80 30" fill="none">
                      <polyline points="0,25 16,20 32,22 48,12 64,8 80,5" stroke="#8B2635" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="lp-mock-stat">
                    <span className="lp-mock-stat-label">Session Time</span>
                    <span className="lp-mock-stat-val">04:12</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── Sample Cards Strip ── */}
      <section className="lp-sample-strip">
        <div className="lp-sample-strip-inner">
          <p className="lp-sample-strip-label">Real photos used in every session</p>
          <div className="lp-sample-grid">
            {SAMPLE_CARDS.map(card => (
              <div key={card.label} className="lp-sample-card">
                <img
                  src={`https://images.pexels.com/photos/${card.pxId}/pexels-photo-${card.pxId}.jpeg?auto=compress&cs=tinysrgb&w=200&h=180&fit=crop`}
                  alt={card.label}
                  className="lp-sample-img"
                />
                <span className="lp-sample-label">{card.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <div className="lp-stats-bar">
        <div className="lp-stats-inner">
          {STATS.map((s) => (
            <div key={s.label} className="lp-stat">
              <span className="lp-stat-value">{s.value}</span>
              <span className="lp-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Features ── */}
      <section id="features" className="lp-features">
        <div className="lp-features-header">
          <span className="lp-section-label">Capabilities</span>
          <h2 className="lp-section-title">Everything You Need to Run Effective Sessions</h2>
          <div className="lp-section-divider" aria-hidden="true">
            <span className="lp-section-divider-line" />
            <svg width="14" height="14" viewBox="0 0 28 28" fill="none">
              <path d="M14 2C14 2 15.2 8.8 18 11C20.8 13.2 28 14 28 14C28 14 20.8 14.8 18 17C15.2 19.2 14 26 14 26C14 26 12.8 19.2 10 17C7.2 14.8 0 14 0 14C0 14 7.2 13.2 10 11C12.8 8.8 14 2 14 2Z" fill="#8B2635" opacity="0.7"/>
            </svg>
            <span className="lp-section-divider-line" />
          </div>
          <p className="lp-section-sub">
            Built on the same principles BCBAs use in clinic — now available to anyone supporting a child's learning.
          </p>
        </div>
        <div className="lp-features-grid">
          {FEATURES.map((f) => (
            <div key={f.title} className="lp-feature-card">
              <div className="lp-feature-icon-wrap">
                {f.icon}
              </div>
              <h3 className="lp-feature-title">{f.title}</h3>
              <p className="lp-feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Create Your Own Decks ── */}
      <section className="lp-custom-deck">
        <div className="lp-custom-deck-inner">

          <div className="lp-custom-deck-text">
            <span className="lp-section-label">Build Your Own</span>
            <h2 className="lp-custom-deck-title">Create and Edit Your Own Decks</h2>
            <p className="lp-custom-deck-sub">
              Beyond the built-in library, you can build completely custom decks for any skill area.
              Upload photos from your device, search Pexels, or take a photo with your camera —
              then add labels and variations just like the built-in cards.
            </p>
            <div className="lp-custom-deck-steps">
              <div className="lp-custom-step">
                <div className="lp-custom-step-num">1</div>
                <div>
                  <strong>Name your deck</strong>
                  <p>Give it a title and choose an icon and color</p>
                </div>
              </div>
              <div className="lp-custom-step">
                <div className="lp-custom-step-num">2</div>
                <div>
                  <strong>Add cards with photos</strong>
                  <p>Search the web, use your camera, or upload from device</p>
                </div>
              </div>
              <div className="lp-custom-step">
                <div className="lp-custom-step-num">3</div>
                <div>
                  <strong>Run sessions instantly</strong>
                  <p>Your deck appears in your library the moment you save</p>
                </div>
              </div>
            </div>
            <button className="lp-btn-primary" onClick={() => scrollTo('try')}>
              Get Started — Build Your First Deck
            </button>
          </div>

          <div className="lp-custom-deck-visual">
            <div className="lp-custom-deck-photo-frame">
              <img
                src="https://images.pexels.com/photos/4144034/pexels-photo-4144034.jpeg?auto=compress&cs=tinysrgb&w=500&h=380&fit=crop"
                alt="Creating a custom therapy deck on a tablet"
                className="lp-custom-deck-photo"
              />
              <div className="lp-custom-deck-badge">
                <div className="lp-custom-deck-badge-icon">+</div>
                <div>
                  <div className="lp-custom-deck-badge-title">New Card Added</div>
                  <div className="lp-custom-deck-badge-sub">Dog — Animals Deck</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── Who It's For ── */}
      <section id="who" className="lp-who">
        <div className="lp-who-header">
          <span className="lp-section-label">Who It's For</span>
          <h2 className="lp-section-title">Built for Everyone in the Child's Circle</h2>
          <div className="lp-section-divider" aria-hidden="true">
            <span className="lp-section-divider-line" />
            <svg width="14" height="14" viewBox="0 0 28 28" fill="none"><path d="M14 2C14 2 15.2 8.8 18 11C20.8 13.2 28 14 28 14C28 14 20.8 14.8 18 17C15.2 19.2 14 26 14 26C14 26 12.8 19.2 10 17C7.2 14.8 0 14 0 14C0 14 7.2 13.2 10 11C12.8 8.8 14 2 14 2Z" fill="#8B2635" opacity="0.7"/></svg>
            <span className="lp-section-divider-line" />
          </div>
          <p className="lp-section-sub">
            Whether you're a BCBA, a classroom teacher, or a parent doing homework drills — Stimuli Central works for you.
          </p>
        </div>
        <div className="lp-who-grid">
          {WHO.map((w) => (
            <div key={w.title} className="lp-who-card">
              <img className="lp-who-photo" src={w.photo} alt={w.title} />
              <div className="lp-who-card-body">
                <h3 className="lp-who-card-title">{w.title}</h3>
                <p className="lp-who-card-desc">{w.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how" className="lp-how">
        <div className="lp-how-header">
          <span className="lp-section-label">How It Works</span>
          <h2 className="lp-section-title">Up and Running in Under 3 Minutes</h2>
          <div className="lp-section-divider" aria-hidden="true">
            <span className="lp-section-divider-line" />
            <svg width="14" height="14" viewBox="0 0 28 28" fill="none"><path d="M14 2C14 2 15.2 8.8 18 11C20.8 13.2 28 14 28 14C28 14 20.8 14.8 18 17C15.2 19.2 14 26 14 26C14 26 12.8 19.2 10 17C7.2 14.8 0 14 0 14C0 14 7.2 13.2 10 11C12.8 8.8 14 2 14 2Z" fill="#8B2635" opacity="0.7"/></svg>
            <span className="lp-section-divider-line" />
          </div>
        </div>
        <div className="lp-how-steps">
          {STEPS.map((s, i) => (
            <div key={s.num} className="lp-step">
              <div className="lp-step-num">{s.num}</div>
              <h3 className="lp-step-title">{s.title}</h3>
              <p className="lp-step-desc">{s.desc}</p>
              {i < STEPS.length - 1 && <div className="lp-step-arrow">→</div>}
            </div>
          ))}
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="lp-testimonials">
        <div className="lp-testimonials-header">
          <span className="lp-section-label">Testimonials</span>
          <h2 className="lp-section-title">Trusted by Therapists, Teachers & Families</h2>
        </div>
        <div className="lp-testimonials-grid">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="lp-testimonial-card">
              <p className="lp-testimonial-quote">"{t.quote}"</p>
              <div className="lp-testimonial-author">
                <div className="lp-testimonial-avatar">{t.initial}</div>
                <div>
                  <div className="lp-testimonial-name">{t.name}</div>
                  <div className="lp-testimonial-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="lp-faq">
        <div className="lp-faq-header">
          <span className="lp-section-label">Questions</span>
          <h2 className="lp-section-title">Frequently Asked Questions</h2>
        </div>
        <div className="lp-faq-list">
          {FAQS.map((f) => (
            <FaqItem key={f.q} q={f.q} a={f.a} />
          ))}
        </div>
      </section>

      {/* ── Try It Free ── */}
      <TrySection navigate={navigate} />

      {/* ── CTA ── */}
      <section className="lp-cta">
        <div className="lp-cta-inner">
          <h2 className="lp-cta-title">Ready to Run Better Sessions?</h2>
          <p className="lp-cta-sub">
            Join therapists, educators, and caregivers already using Stimuli Central to teach real generalization.
          </p>
          <button className="lp-cta-btn" onClick={() => scrollTo('try')}>
            Sign Up Now — Free Trial →
          </button>
          <p className="lp-cta-fine">2-week trial · Full access from day one</p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <LogoLockup size={26} />
            <p className="lp-footer-tagline">Evidence-based ABA stimulus training for everyone.</p>
          </div>

          <div className="lp-footer-cols">
            <div className="lp-footer-col">
              <div className="lp-footer-col-title">Product</div>
              <button className="lp-footer-link" onClick={() => scrollTo('features')}>Features</button>
              <button className="lp-footer-link" onClick={() => scrollTo('how')}>How It Works</button>
              <button className="lp-footer-link" onClick={() => scrollTo('try')}>Free Trial</button>
            </div>
            <div className="lp-footer-col">
              <div className="lp-footer-col-title">Support</div>
              <button className="lp-footer-link" onClick={() => scrollTo('faq')}>FAQ</button>
              <a href="mailto:hello@stimulicentral.org" className="lp-footer-link">Contact Us</a>
            </div>
            <div className="lp-footer-col">
              <div className="lp-footer-col-title">Legal</div>
              <a href="/privacy" className="lp-footer-link">Privacy Policy</a>
              <a href="/terms" className="lp-footer-link">Terms of Service</a>
            </div>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <span>© 2026 Stimuli Central. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
};
